import React from 'react';
import { Box, Divider, Grid, Paper } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import {
    DateFilterContainer, DateFromToPicker, QueryContextProvider,
    ResetCallback, UpdateFromCallback, UpdateToCallback, useFromToContext,
} from '../../shared';
import { GET_POHODA_ISSUE_SLIP, GET_PRODUCTS_REPORT } from '../graphql';
import { DownloadCsvIconButton, DownloadXmlIconButton } from '../../../ui-kit';
import { useUser } from '../../../utils/hooks/auth';
import { Venue } from '../../../server-types';
import { READ_POHODA_CENTRE_ID } from '../../../graphql/queries';
import { useDateTimeService } from '../../../utils/hooks/dateTimeService';
import { setUrlParams } from '../../../utils/helpers/urlParams';
import { OrderingContextProvider } from '../hooks';
import { ProductStatisticsTable } from './ProductStatisticsTableView';

const ProductStatistics = () => {
    const matches = useMediaQuery('(min-width:650px)');
    const history = useHistory();
    const { t } = useTranslation();
    const { dateFromTo, setDateFromTo } = useFromToContext();
    const { user } = useUser();
    const client = useApolloClient();
    const { dateTimeService } = useDateTimeService();

    const updateFromContext: UpdateFromCallback = (from, dateTo) => {
        if (from) {
            const params = setUrlParams(history.location.search, {
                from: dateTimeService.getIsoDateStringFromJsDate(from),
            });
            history.push({ search: params.toString() });
        }

        if (from && dateTo && (from.valueOf() <= dateTo.valueOf())) {
            setDateFromTo({ from, to: dateTo.toJSDate() });
        }
        if (from === null) {
            resetContext();
        }
    };

    const updateToContext: UpdateToCallback = (to, dateFrom) => {
        if (to) {
            const params = setUrlParams(history.location.search, {
                to: dateTimeService.getIsoDateStringFromJsDate(to),
            });
            history.push({ search: params.toString() });
        }
        if (to && dateFrom && (to.valueOf() >= dateFrom.valueOf())) {
            setDateFromTo({ to, from: dateFrom.toJSDate() });
        }
        if (to === null) {
            resetContext();
        }
    };

    const venue = client.readFragment<Venue>({
        id: `Venue:${user.currentVenueId}`,
        fragment: READ_POHODA_CENTRE_ID,
    });

    const showPohodaExport = venue && !!venue.pohodaCentreId;

    const resetContext: ResetCallback = () => {
        setDateFromTo(undefined);
    };

    let startDate;
    let endDate;
    if (dateFromTo) {
        startDate = dateTimeService.getDateTimeFromJsDate(dateFromTo.from);
        endDate = dateTimeService.getDateTimeFromJsDate(dateFromTo.to);
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Box p={2} component={Paper}>
                    <Box display='flex' alignItems='center' flexWrap='wrap'>
                        <Box width={320}>
                            <DateFromToPicker
                                startDate={startDate}
                                endDate={endDate}
                                updateToCallback={updateToContext}
                                updateFromCallback={updateFromContext}
                                hideReset
                            />
                        </Box>
                        {matches
                            ? (
                                <Box display='flex' alignSelf='stretch' mx={2}>
                                    <Divider flexItem orientation='vertical' />
                                </Box>
                            )
                            : null}
                        <Box>
                            <DownloadCsvIconButton
                                query={GET_PRODUCTS_REPORT}
                                propertyKey='productsReport'
                                fileName={t('product_report')}
                                variables={{ dateFromTo }}
                            />
                        </Box>
                        {showPohodaExport
                            ? (
                                <>
                                    <Box display='flex' alignSelf='stretch' mx={2}>
                                        <Divider flexItem orientation='vertical' />
                                    </Box>
                                    <Box>
                                        <DownloadXmlIconButton
                                            label={t('Pohoda Export')}
                                            query={GET_POHODA_ISSUE_SLIP}
                                            propertyKey='pohodaIssueSlip'
                                            fileName={t('pohoda_product_report')}
                                            variables={{ dateFromTo }}
                                        />
                                    </Box>
                                </>
                            ): null}
                    </Box>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <ProductStatisticsTable />
            </Grid>
        </Grid>
    );
};

export default () => (
    <DateFilterContainer disableWrapper>
        <QueryContextProvider>
            <OrderingContextProvider>
                <ProductStatistics />
            </OrderingContextProvider>
        </QueryContextProvider>
    </DateFilterContainer>
);
